import {
  computed,
  effect,
  inject,
  Injectable,
  OnDestroy,
  signal,
} from '@angular/core';
import { Observable, tap } from 'rxjs';
import { toBig } from '@core/utils/big-number.util';
import { CartItem } from './cart-item.model';
import { CartService } from './cart.service';
import {
  CreateBuyRequestByWalletRequest,
  CreateBuyRequestRequest,
} from './shopping-cart.request';
import {
  CreateBuyRequestByWalletResponse,
  CreateBuyRequestResponse,
} from './shopping-cart.response';

export interface NewCartItemData {
  name: string;
  typeName: string;
  currencyName: string;
  logoUrl: string | null;
  totalAmount?: string;
}

@Injectable({ providedIn: 'root' })
export class CartStore implements OnDestroy {
  private readonly _api = inject(CartService);

  private readonly _items = signal<CartItem[]>([]);
  private readonly _loading = signal(false);
  private readonly _initialized = signal(false);
  private readonly _error = signal<unknown>(undefined);
  private readonly _pendingIds = signal<Set<string>>(new Set());
  private readonly _rialCurrencyName = signal('');
  private readonly _showCommission = signal(true);
  private readonly _rialTotals = signal<Map<string, string>>(new Map());
  private readonly _expireAt = signal('');
  private readonly _expireInSecond = signal(0);
  private readonly _token = signal('');
  private readonly _now = signal(Date.now());
  private _tickId: ReturnType<typeof setInterval> | null = null;
  private _reloadTimerId: ReturnType<typeof setTimeout> | null = null;

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly initialized = this._initialized.asReadonly();
  readonly error = this._error.asReadonly();
  readonly pendingIds = this._pendingIds.asReadonly();
  readonly rialCurrencyName = this._rialCurrencyName.asReadonly();
  readonly showCommission = this._showCommission.asReadonly();
  readonly rialTotals = this._rialTotals.asReadonly();
  readonly expireAt = this._expireAt.asReadonly();
  readonly expireInSecond = this._expireInSecond.asReadonly();
  readonly token = this._token.asReadonly();

  readonly totalSeconds = this._expireInSecond.asReadonly();

  readonly remainingSeconds = computed(() => {
    const expireAt = this._expireAt();
    if (!expireAt) return 0;
    const ms = new Date(expireAt).getTime() - this._now();
    return Math.max(0, Math.floor(ms / 1000));
  });

  readonly itemCount = computed(() => {
    const items = this._items();
    return items.reduce((sum, item) => {
      const qty = item.giftCard?.quantity ?? item.bundle?.quantity ?? 0;
      return sum + qty;
    }, 0);
  });

  readonly totalPayable = computed(() => {
    const items = this._items();
    if (!items.length) return '0';
    return items
      .reduce((sum, item) => {
        if (item.bundleItems) {
          const bundleQty = item.bundle?.quantity ?? 1;
          const bundleSum = item.bundleItems.reduce(
            (bSum, bi) => bSum.plus(bi.calculation?.finalPaymentAmount ?? '0'),
            toBig(0),
          );
          return sum.plus(bundleSum.times(bundleQty));
        }
        return sum.plus(item.calculation?.finalPaymentAmount ?? '0');
      }, toBig(0))
      .toString();
  });

  readonly currencyName = computed(() => {
    const items = this._items();
    if (!items.length) return '';
    const first = items[0];
    return (
      first.giftCard?.currency?.name ??
      first.bundle?.giftCards?.[0]?.currency?.name ??
      ''
    );
  });

  findGiftCardItem(
    serviceProviderItemId: string,
    amount: string | null,
  ): CartItem | null {
    const items = this._items();
    return (
      items.find(
        (ci) =>
          ci.giftCard?.serviceProviderItemId === serviceProviderItemId &&
          ci.giftCard?.amount === amount,
      ) ?? null
    );
  }

  findBundleItem(bundleId: string): CartItem | null {
    const items = this._items();
    return items.find((ci) => ci.bundle?.bundleId === bundleId) ?? null;
  }

  constructor() {
    effect(() => {
      const expireAt = this._expireAt();
      if (!expireAt) return;
      this._startReloadTimer(expireAt);
    });

    this._tickId = setInterval(() => {
      this._now.set(Date.now());
    }, 1000);
  }

  ngOnDestroy(): void {
    this._clearTimers();
  }

  load(complete?: () => void): void {
    this._loading.set(true);
    this._error.set(undefined);
    this._api.getCart().subscribe({
      next: (response) => {
        this._items.set(response.items);
        this._rialCurrencyName.set(response.rialCurrencyName);
        this._showCommission.set(response.showCommission);
        this._rialTotals.set(response.rialTotals);
        this._expireAt.set(response.expireAt);
        this._expireInSecond.set(response.expireInSecond);
        this._token.set(response.token);
        this._initialized.set(true);
      },
      error: (err) => this._error.set(err),
      complete: () => {
        this._loading.set(false);
        complete?.();
      },
    });
  }

  clear(): void {
    this._clearReloadTimer();
    this._items.set([]);
    this._initialized.set(false);
    this._expireAt.set('');
    this._expireInSecond.set(0);
    this._token.set('');
  }

  addGiftCard(
    serviceProviderItemId: string,
    amount: string | null,
    _itemData?: NewCartItemData,
    pendingItemId?: string,
    onSuccess?: () => void,
    onError?: () => void,
  ): void {
    if (pendingItemId !== undefined) {
      this._pendingIds.update((s) => new Set(s).add(pendingItemId));
    }
    this._api.addGiftCard(serviceProviderItemId, amount).subscribe({
      next: () =>
        this.load(() => {
          if (pendingItemId !== undefined) {
            this._pendingIds.update((s) => {
              const n = new Set(s);
              n.delete(pendingItemId);
              return n;
            });
          }
          onSuccess?.();
        }),
      error: () => {
        if (pendingItemId !== undefined) {
          this._pendingIds.update((s) => {
            const n = new Set(s);
            n.delete(pendingItemId);
            return n;
          });
        }
        onError?.();
      },
    });
  }

  addBundle(
    bundleId: string,
    _itemData?: NewCartItemData,
    onSuccess?: () => void,
    onError?: () => void,
  ): void {
    this._api.addBundle(bundleId).subscribe({
      next: () => this.load(onSuccess),
      error: () => onError?.(),
    });
  }

  removeItem(
    itemId: string,
    onFinally?: () => void,
    onError?: () => void,
  ): void {
    this._pendingIds.update((s) => new Set(s).add(itemId));
    this._api.removeItem(itemId).subscribe({
      complete: () =>
        this.load(() => {
          this._pendingIds.update((s) => {
            const n = new Set(s);
            n.delete(itemId);
            return n;
          });
          onFinally?.();
        }),
      error: () => {
        this._pendingIds.update((s) => {
          const n = new Set(s);
          n.delete(itemId);
          return n;
        });
        onError?.();
        onFinally?.();
      },
    });
  }

  decrementGiftCard(item: CartItem, onFinally?: () => void): void {
    this._pendingIds.update((s) => new Set(s).add(item.shoppingCartItemId));
    this._api.removeItem(item.shoppingCartItemId).subscribe({
      complete: () =>
        this.load(() => {
          this._pendingIds.update((s) => {
            const n = new Set(s);
            n.delete(item.shoppingCartItemId);
            return n;
          });
          onFinally?.();
        }),
      error: () => {
        this._pendingIds.update((s) => {
          const n = new Set(s);
          n.delete(item.shoppingCartItemId);
          return n;
        });
        onFinally?.();
      },
    });
  }

  buyCart(
    request: CreateBuyRequestRequest,
  ): Observable<CreateBuyRequestResponse> {
    return this._api.buyCart(request);
  }

  buyCartByWallet(
    request: CreateBuyRequestByWalletRequest,
  ): Observable<CreateBuyRequestByWalletResponse> {
    return this._api.buyCartByWallet(request).pipe(
      tap(() => {
        this.clear();
        this.load();
      }),
    );
  }

  private _startReloadTimer(expireAt: string): void {
    this._clearReloadTimer();
    const expiryMs = new Date(expireAt).getTime();
    if (isNaN(expiryMs) || expiryMs <= Date.now()) {
      this.load();
      return;
    }
    const delayMs = expiryMs - Date.now();
    this._reloadTimerId = setTimeout(() => {
      this._reloadTimerId = null;
      this.load();
    }, delayMs);
  }

  private _clearReloadTimer(): void {
    if (this._reloadTimerId !== null) {
      clearTimeout(this._reloadTimerId);
      this._reloadTimerId = null;
    }
  }

  private _clearTimers(): void {
    if (this._tickId !== null) {
      clearInterval(this._tickId);
      this._tickId = null;
    }
    this._clearReloadTimer();
  }
}
